import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query'
import { GridFilterOperatorEnum } from 'types/GridFilterOperatorEnum'

import { datePattern, dateTimePattern } from './date'
import { isNumeric } from './is-numeric'
import { isString } from './is-string'
import { isNotNullOrUndefined } from './isNotNullOrUndefined'
import { zonedTimeToUtc } from './time-zone/helpers'

export type BuildQueryProps = CompositeFilterDescriptor & {
  defaultFilters?: CompositeFilterDescriptor
}

type CompoundFilterDescriptor =
  | FilterDescriptor
  | CompositeFilterDescriptor
  | FilterDescriptor[]
  | CompositeFilterDescriptor[]

export const FilterSpecialCharactersPattern = /[,]/i
export const FilterSpecialOperatorsPattern = /^or$|^and$/i

export enum FilterLogic {
  OR = 'or',
  AND = 'and',
}

export enum SortLogic {
  ASC = 'asc',
  DESC = 'desc',
  NONE = 'none',
}

export const getSortLogic = (sortLogic: string): SortLogic => {
  switch (sortLogic?.toLowerCase()) {
    case SortLogic.ASC:
      return SortLogic.ASC
    case SortLogic.DESC:
      return SortLogic.DESC
    default:
      return SortLogic.NONE
  }
}

export const isSortAscLogic = (sortLogic: string): boolean => {
  return getSortLogic(sortLogic) === SortLogic.ASC
}

export const isSortDescLogic = (sortLogic: string): boolean => {
  return getSortLogic(sortLogic) === SortLogic.DESC
}

export const buildQuery = (filterDescriptor: BuildQueryProps): string | null => {
  const filterString: string | null = null

  if (
    filterDescriptor?.defaultFilters &&
    instanceOfCompositeFilterDescriptorByLogic(filterDescriptor) &&
    instanceOfCompositeFilterDescriptorByLogic(filterDescriptor.defaultFilters)
  ) {
    if (
      filterDescriptor?.filters?.length === 0 &&
      filterDescriptor?.defaultFilters?.filters.length === 0
    ) {
      return filterString
    }
  }

  const filters = removeEmptyFilters(filterDescriptor)
  const defaultFilters = removeEmptyDefaultFilters(filterDescriptor)
  const searchQuery = buildSearchQuery(filters, filters)

  if (defaultFilters?.defaultFilters) {
    const defaultSearchQuery = buildSearchQuery(
      defaultFilters.defaultFilters,
      defaultFilters.defaultFilters
    )

    if (defaultSearchQuery && searchQuery) {
      return `(${defaultSearchQuery}) AND (${searchQuery})`
    }

    if (defaultSearchQuery) {
      return defaultSearchQuery
    }
  }

  return searchQuery || null
}

type FilterDescriptorParams = {
  allFilterDescriptor: CompoundFilterDescriptor
  filters: CompoundFilterDescriptor[]
  parentLogic: string
  isCurrentSingleFilter: boolean
  isCurrentMultipleFilter: boolean
  isCurrentGroupedSingleFilter: boolean
  isCurrentGroupedMultipleFilter: boolean
  isNextGroupedSingleFilter: boolean
  isNextGroupedMultipleFilter: boolean
  isCurrentGroupedFilter: boolean
  isNextGroupedFilter: boolean
  hasNextFilter: boolean
  hasNextGroupedFilter: boolean
}

const buildSearchQuery = (
  allFilterDescriptor: CompoundFilterDescriptor,
  filterDescriptor: CompoundFilterDescriptor
) => {
  if (isGroupedFilters(filterDescriptor)) {
    if (!filterDescriptor?.filters) return null

    const parentLogic = filterDescriptor.logic.toUpperCase()
    const filters = filterDescriptor.filters

    const [currentFilter, nextFilter] = filters
    const isCurrentSingleFilter = isSingleFilterDescriptor(filterDescriptor)
    const isCurrentMultipleFilter = isMultipleFilterDescriptor(filterDescriptor)

    const isCurrentGroupedSingleFilter = isSingleFilterDescriptor(currentFilter)
    const isCurrentGroupedMultipleFilter = isMultipleFilterDescriptor(currentFilter)

    const isNextGroupedSingleFilter = isSingleFilterDescriptor(nextFilter)
    const isNextGroupedMultipleFilter = isMultipleFilterDescriptor(nextFilter)

    const isCurrentGroupedFilter = isGroupedFilters(currentFilter)
    const isNextGroupedFilter = isGroupedFilters(nextFilter)

    const hasNextFilter = isFilterDescriptor(nextFilter) ? !!nextFilter?.value : false
    const hasNextGroupedFilter = isGroupedFilters(nextFilter)
      ? hasGroupedFilterDescriptor(nextFilter)
      : false

    return buildQueryFilters({
      allFilterDescriptor,
      filters,
      parentLogic,
      isCurrentSingleFilter,
      isCurrentMultipleFilter,
      isCurrentGroupedSingleFilter,
      isCurrentGroupedMultipleFilter,
      isNextGroupedSingleFilter,
      isNextGroupedMultipleFilter,
      isCurrentGroupedFilter,
      isNextGroupedFilter,
      hasNextFilter,
      hasNextGroupedFilter,
    })
  }
}

const buildQueryFilters = ({ ...props }: FilterDescriptorParams): string | null => {
  const {
    filters,
    isCurrentSingleFilter,
    isCurrentMultipleFilter,
    hasNextFilter,
    hasNextGroupedFilter,
    allFilterDescriptor,
    parentLogic,
  } = props

  const searchQuery = ''
  const newFilters = filters
    .map((filter) => {
      if (isFilterDescriptor(filter) && isString(filter?.value)) {
        return skipSpecialCharactersFromFilter(filter)
      }
      if (isFilterDescriptors(filter)) {
        return filter.map((x) => {
          if (isFilterDescriptor(x) && isString(x?.value)) {
            return skipSpecialCharactersFromFilter(x)
          }
          return x
        })
      }
      return filter
    })
    .filter(isFilterEmpty)

  const sortedFilters = [
    ...newFilters.filter((filter) => !isGroupedFilters(filter)),
    ...newFilters.filter((filter) => isGroupedFilters(filter)),
  ]

  return sortedFilters.reduce((previous, current) => {
    if (isGroupedFilters(current)) {
      const queryFilters = buildSearchQuery(allFilterDescriptor, current)

      if (!queryFilters) {
        return previous
      }

      if (!previous) {
        return queryFilters
      }

      if (isCurrentMultipleFilter && parentLogic === FilterLogic.AND.toUpperCase()) {
        return `${previous} ${FilterLogic.AND.toUpperCase()} (${queryFilters})`
      }

      if (isCurrentMultipleFilter && parentLogic === FilterLogic.OR.toUpperCase()) {
        return `(${previous}) ${FilterLogic.AND.toUpperCase()} (${queryFilters})`
      }

      if (
        isCurrentSingleFilter &&
        (parentLogic === FilterLogic.AND.toUpperCase() ||
          parentLogic === FilterLogic.OR.toUpperCase())
      ) {
        return `(${previous}) ${FilterLogic.AND.toUpperCase()} (${queryFilters})`
      }

      return previous
    }

    if (instanceOfFilterDescriptor(current)) {
      if (!isNotNullOrUndefined(current.value)) {
        if (
          current.operator === GridFilterOperatorEnum.isEmptyOperator ||
          current.operator === GridFilterOperatorEnum.isNotEmptyOperator
        ) {
          return `${current.field} ${current.operator} 1`
        }
        return previous
      }

      if (isFilterExist(current) && current.field) {
        const currentValue = formatFilterValue(current.value, current.field)

        // if "no previous" data found, only one filter is selected
        if (
          !previous &&
          instanceOfCompositeFilterDescriptor(allFilterDescriptor) &&
          allFilterDescriptor?.filters?.length === 1
        ) {
          return `${current.field} ${current.operator} ${currentValue}`
        }

        // if "no previous" data found, only one filter is selected and grouped filter is empty
        if (!previous && isCurrentSingleFilter && !hasNextFilter && !hasNextGroupedFilter) {
          return `${current.field} ${current.operator} ${currentValue}`
        }

        if (!previous) {
          if (isCurrentMultipleFilter) {
            return `(${current.field} ${current.operator} ${currentValue})`
          } else {
            return `${current.field} ${current.operator} ${currentValue}`
          }
        }

        // if parent filter options has a logic operator
        if (previous && parentLogic) {
          return `${previous} ${parentLogic} (${current.field} ${current.operator} ${currentValue})`
        }
      }
    }
    return searchQuery
  }, searchQuery)
}

const skipSpecialCharactersFromFilter = (x: FilterDescriptor): FilterDescriptor => {
  if (
    (x.field === 'CountryId' ||
      x.field === 'country' ||
      x.field === 'Country' ||
      x.field === 'countryCode' ||
      x.field === 'Details.PrimaryCountry') &&
    x?.value === 'AND'
  ) {
    return x
  }

  return {
    ...x,
    value: x?.value
      ?.replace(FilterSpecialCharactersPattern, '')
      .replace(FilterSpecialOperatorsPattern, '')
      .trim(),
  }
}

type FilterValue = string | boolean | Date | { id: string }[]

// eslint-disable-next-line @typescript-eslint/ban-types
const formatFilterValue = (value: FilterValue, field: string | Function) => {
  if (field && typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }

  // list of instances with ID, example: [{ id: '1' }, { id: '2' }]
  if (isArrayOfInstanceID(value)) {
    return value
      .filter((x) => !!x)
      .map((x) => x.id)
      .join(',')
  }

  // default "array" value
  if (field && value && isNotNullOrUndefined(value) && Array.isArray(value)) {
    return value.length > 0 ? value.join(',').trim() : null
  }

  // format date+time with type "Date" to UTC
  if (field && value && value instanceof Date && dateTimePattern.test(value.toISOString())) {
    const utcDate = zonedTimeToUtc(value)
    return utcDate.toISOString()
  }

  // format date with type "Date" to UTC
  if (field && value && value instanceof Date && datePattern.test(value.toISOString())) {
    const utcDate = zonedTimeToUtc(value)
    return utcDate.toISOString()
  }

  // format date+time with type "string" to UTC
  if (field && value && isString(value) && dateTimePattern.test(value)) {
    return new Date(value).toISOString()
  }

  // format date with type "string" to UTC
  if (field && value && isString(value) && datePattern.test(value)) {
    return new Date(value).toISOString()
  }

  // default "string" value
  if (field && value && isNotNullOrUndefined(value) && typeof field === 'string') {
    return `${value}`.trim()
  }

  return value
}

const removeEmptyFilters = (
  filterDescriptor: CompositeFilterDescriptor
): CompositeFilterDescriptor => {
  const compositeFilter = {
    logic: filterDescriptor.logic,
    filters: (filterDescriptor?.filters || []).filter(isFilterEmpty).map((currentValue) => {
      if (isGroupedFilters(currentValue)) {
        return removeEmptyFilters(currentValue)
      }
      if (instanceOfFilterDescriptor(currentValue)) {
        return currentValue
      }

      return currentValue
    }),
  }

  return {
    ...filterDescriptor,
    filters: compositeFilter.filters,
  }
}

const removeEmptyDefaultFilters = (filterDescriptor: BuildQueryProps): BuildQueryProps => {
  const defaultLogic = filterDescriptor.defaultFilters?.logic || FilterLogic.AND
  const defaultFilters = (filterDescriptor?.defaultFilters?.filters || [])
    .filter(isFilterEmpty)
    .map((currentValue) => {
      if (isGroupedFilters(currentValue)) {
        return removeEmptyFilters(currentValue)
      }
      if (instanceOfFilterDescriptor(currentValue)) {
        return currentValue
      }

      return currentValue
    })

  return {
    logic: filterDescriptor.logic,
    filters: filterDescriptor.filters,
    defaultFilters: {
      logic: defaultLogic,
      filters: defaultFilters,
    },
  }
}

const isFilterEmpty = (filter: CompoundFilterDescriptor): boolean => {
  if (instanceOfFilterDescriptor(filter)) {
    return isFilterDescriptorValueEmpty(filter)
  }
  if (isFilterDescriptors(filter)) {
    return filter.map((x) => isFilterDescriptorValueEmpty(x)).every((x) => x)
  }
  return true
}

const isFilterDescriptorValueEmpty = (filter: CompoundFilterDescriptor): boolean => {
  if (instanceOfFilterDescriptor(filter)) {
    if (
      filter.operator === GridFilterOperatorEnum.isEmptyOperator ||
      filter.operator === GridFilterOperatorEnum.isNotEmptyOperator
    ) {
      return true
    }
    if (Array.isArray(filter.value)) {
      return filter?.value?.length >= 1
    }
    if (isString(filter.value)) {
      return filter.value.length >= 1
    }
    if (filter.value !== null) {
      return filter.value?.toString()
    }
    return !!filter?.value
  }
  return false
}

export const buildSortQuery = (sortDescriptors: SortDescriptor[]): string | null => {
  return sortDescriptors
    .filter((x) => !!x.field && !!x.dir)
    .map((x) => `${x.field} ${x.dir}`)
    .join(',')
}

const isSingleFilterDescriptor = (
  object: CompoundFilterDescriptor
): object is FilterDescriptor[] => {
  return (
    object && 'filters' in object && object.filters.filter((x) => !('filters' in x)).length === 1
  )
}

const isMultipleFilterDescriptor = (
  object: CompoundFilterDescriptor
): object is FilterDescriptor[] => {
  return object && 'filters' in object && object.filters.filter((x) => !('filters' in x)).length > 1
}

const hasGroupedFilterDescriptor = (
  object: CompoundFilterDescriptor
): object is FilterDescriptor[] => {
  return (
    object && 'filters' in object && object.filters.filter((x) => !('filters' in x)).length >= 1
  )
}

export const isGroupedFilters = (
  object: CompoundFilterDescriptor
): object is CompositeFilterDescriptor => {
  return isNotNullOrUndefined(object) && 'logic' in object && 'filters' in object
}

const isFilterExist = (object: FilterDescriptor): object is FilterDescriptor => {
  return !!object?.field && isNotNullOrUndefined(object.value)
}

export const isFilterDescriptor = (
  filterItem: CompoundFilterDescriptor
): filterItem is FilterDescriptor => {
  return (filterItem as FilterDescriptor)?.field !== undefined
}

export const isFilterDescriptors = (
  filterItems: CompoundFilterDescriptor | CompoundFilterDescriptor[]
): filterItems is FilterDescriptor[] => {
  const filters = filterItems as FilterDescriptor[]
  if (filters && Array.isArray(filterItems) && filters?.length >= 1) {
    return filters.map((x) => isFilterDescriptor(x)).every((x) => !!x)
  }
  return false
}

export const instanceOfFilterDescriptor = (
  object: CompoundFilterDescriptor
): object is FilterDescriptor => {
  return 'field' in object
}

export const instanceOfCompositeFilterDescriptor = (
  object: CompoundFilterDescriptor
): object is CompositeFilterDescriptor => {
  return 'filters' in object
}

const instanceOfCompositeFilterDescriptorByLogic = (
  object: FilterDescriptor | CompositeFilterDescriptor
): object is CompositeFilterDescriptor => {
  return 'logic' in object
}

const isArrayOfInstanceID = (listOfValue: FilterValue): listOfValue is { id: string }[] => {
  if (Array.isArray(listOfValue)) {
    const valueIds = listOfValue.map((x) => x.id).filter((x) => !!x)
    return valueIds.length === listOfValue.length
  }
  return false
}

export const buildSaveFilter = (filter: CompositeFilterDescriptor): string => {
  if (isGroupedFilters(filter))
    return JSON.stringify({ ...filter, filters: buildSaveGroupFilters(filter.filters) })
  return JSON.stringify(filter)
}

const buildSaveGroupFilters = (
  filters: (FilterDescriptor | CompositeFilterDescriptor)[]
): (FilterDescriptor | CompositeFilterDescriptor)[] =>
  filters.map((filter) => {
    if (isGroupedFilters(filter))
      return { ...filter, filters: buildSaveGroupFilters(filter.filters) }
    if (instanceOfFilterDescriptor(filter) && isString(filter.field))
      return { ...filter, value: buildSaveDescriptorFilters(filter.value, filter.field) }
    return filter
  })

const buildSaveDescriptorFilters = (value: FilterValue, field: string) => {
  if (!isNotNullOrUndefined(value)) {
    return value
  }

  return formatSaveFilterValue(value, field)
}

// eslint-disable-next-line @typescript-eslint/ban-types
const formatSaveFilterValue = (value: FilterValue, field: string | Function) => {
  if (field && typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }

  if (isNumeric(value)) {
    return Number.parseFloat(value.toString())
  }

  // list of instances with ID, example: [{ id: '1' }, { id: '2' }]
  if (isArrayOfInstanceID(value)) {
    return value.filter((x) => !!x?.id)
  }

  // default "array" value
  if (field && value && isNotNullOrUndefined(value) && Array.isArray(value)) {
    return value.length > 0 ? value : null
  }

  // format array of numbers and strings -> only to array of numbers
  if (field && value && Array.isArray(value) && !value.some(isNaN)) {
    return value.filter((x) => !!x).map((x) => Number.parseFloat(x.toString()))
  }

  const result = formatFilterValue(value, field)

  return result
}
